import React, { useEffect, useMemo, useRef, useState } from "react";
import { Send, ChevronDown, X, MessagesSquare, Users, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${API_URL}/api`;

// AEON (unified) is always first; the 10 specialists are loaded live from the roster.
const AEON = { name: "AEON", title: "Market desk", emoji: "A", color: "#d9ff57" };
const COUNCIL = { name: "COUNCIL", title: "Desk review", emoji: "C", color: "#91d5b0" };

// Per-section: who to default to, and a context-aware prompt placeholder.
const SECTION_HINTS = {
  command:    { agent: "AEON",    ask: "Ask about today’s market, risk, or system state" },
  terminal:   { agent: "AEON",    ask: "Ask for a read on the whole desk" },
  trading:    { agent: "AEGIS",   ask: "Ask about positions, risk, or execution" },
  engines:    { agent: "AEON",    ask: "Ask about a Telegram call, WEEX route, or trade draft" },
  team:       { agent: "AEON",    ask: "Send a question to the desk" },
  stats:      { agent: "NOVA",    ask: "Ask about performance, win rate, or edge" },
  quantum:    { agent: "MAXWELL", ask: "Ask about regime and forward signals" },
  researcher: { agent: "NOVA",    ask: "Ask about a test, strategy, or experiment" },
};

const SECTION_LABEL = {
  command: "Hub", terminal: "Terminal", trading: "Trading", engines: "API Trading",
  team: "Team", stats: "Brain", quantum: "Quantum", researcher: "Lab",
};

export default function AgentBar({ section = "command" }) {
  const [roster, setRoster] = useState([]);
  const [agent, setAgent] = useState(AEON);
  const [picker, setPicker] = useState(false);
  const [open, setOpen] = useState(false);          // conversation panel expanded
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convo, setConvo] = useState([]);           // [{role, content, agent}]
  const [threadId, setThreadId] = useState(null);
  const [elapsed, setElapsed] = useState(0);        // seconds the current reply has been cooking
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const userPicked = useRef(false);
  const timerRef = useRef(null);

  // Tick an elapsed-seconds counter while a reply is in flight — the local model
  // can take 30-60s, so we show progress instead of a silent spinner.
  useEffect(() => {
    if (sending) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [sending]);

  // Load the live roster once.
  useEffect(() => {
    fetch(`${API}/team/roster`)
      .then(r => r.json())
      .then(d => setRoster(d.specialists || []))
      .catch(() => {});
  }, []);

  // Build the full agent list: AEON + specialists + Council.
  const agents = useMemo(() => {
    const specs = (roster || []).map(s => ({
      name: s.name, title: s.title || "Specialist",
      emoji: s.emoji || "◆", color: s.color || "#94a3b8", active: s.active,
    }));
    return [AEON, ...specs, COUNCIL];
  }, [roster]);

  // When the section changes, follow its recommended agent — until the user
  // deliberately picks one, after which we respect their choice.
  useEffect(() => {
    if (userPicked.current) return;
    const want = SECTION_HINTS[section]?.agent || "AEON";
    const found = agents.find(a => a.name === want);
    if (found) setAgent(found);
  }, [section, agents]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // Mobile "stuck copy/paste" fix: a lingering text selection over the output can
    // freeze/ghost the panel on new content. Clear any selection when the convo updates.
    try {
      const sel = window.getSelection && window.getSelection();
      if (sel && sel.rangeCount && scrollRef.current && scrollRef.current.contains(sel.anchorNode)) {
        sel.removeAllRanges();
      }
    } catch (_) { /* noop */ }
  }, [convo, open]);

  const placeholder = SECTION_HINTS[section]?.ask || `Ask ${agent.name}…`;

  function pick(a) {
    userPicked.current = true;
    setAgent(a);
    setPicker(false);
    setThreadId(null);
    inputRef.current?.focus();
  }

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setOpen(true);
    setConvo(prev => [...prev, { role: "user", content: text }]);
    setSending(true);

    try {
      if (agent.name === "COUNCIL") {
        await fetch(`${API}/team/council/run?provider=local`, { method: "POST" });
        // Poll the latest verdict a couple of times.
        let verdict = null;
        for (let i = 0; i < 8 && !verdict; i++) {
          await new Promise(r => setTimeout(r, 4000));
          const d = await fetch(`${API}/team/council/latest`).then(r => r.json()).catch(() => null);
          if (d && (d.summary || d.final_stance)) verdict = d;
        }
        const txt = verdict
          ? `${verdict.summary || "Verdict reached."}\n\n— ${verdict.final_stance || ""} ${verdict.final_confidence ? verdict.final_confidence + "%" : ""}`.trim()
          : "The council is convening — check back in ~30s (Team → Council).";
        setConvo(prev => [...prev, { role: "assistant", content: txt, agent: "COUNCIL" }]);
      } else {
        const res = await fetch(`${API}/team/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            specialist: agent.name, message: text, thread_id: threadId,
            context: SECTION_LABEL[section] || section,
            quick: true,   // lean grounding → snappy replies from the global bar
          }),
        });
        const data = await res.json();
        if (data.error) {
          setConvo(prev => [...prev, { role: "assistant", content: data.error, agent: agent.name, err: true }]);
        } else {
          setThreadId(data.thread_id);
          setConvo(prev => [...prev, { role: "assistant", content: data.reply, agent: agent.name }]);
        }
      }
    } catch {
      setConvo(prev => [...prev, { role: "assistant", content: "Network error — try again.", agent: agent.name, err: true }]);
    }
    setSending(false);
    inputRef.current?.focus();
  }

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="agentbar">
      <div className="agentbar-row">
        {/* Agent selector */}
        <button className="agentbar-agent" onClick={() => setPicker(p => !p)} title="Choose who to talk to">
          <span className="agentbar-emoji" style={{ "--agent-color": agent.color }}>{agent.name.slice(0, 1)}</span>
          <span className="agentbar-agent-meta">
            <span className="agentbar-agent-name">{agent.name}</span>
            <span className="agentbar-agent-title">{agent.title}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </button>

        {/* Input */}
        <div className="agentbar-input-wrap">
          <input
            ref={inputRef}
            className="agentbar-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder={placeholder}
          />
          <button className="agentbar-send" onClick={send} disabled={sending || !input.trim()} title="Send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>

        {convo.length > 0 && (
          <button className="agentbar-toggle" onClick={() => setOpen(o => !o)} title={open ? "Hide" : "Show conversation"}>
            {open ? <X className="h-4 w-4" /> : <MessagesSquare className="h-4 w-4" />}
          </button>
        )}
      </div>

      {/* Agent picker dropdown */}
      {picker && (
        <div className="agentbar-picker" onMouseLeave={() => setPicker(false)}>
          {agents.map(a => (
            <button key={a.name} className={`agentbar-pick ${a.name === agent.name ? "is-active" : ""}`} onClick={() => pick(a)}>
              <span className="agentbar-emoji" style={{ "--agent-color": a.color }}>
                {a.name === "COUNCIL" ? <Users className="h-4 w-4" /> : a.name.slice(0, 1)}
              </span>
              <span className="agentbar-pick-name">{a.name}</span>
              <span className="agentbar-pick-title">{a.title}</span>
              {a.active ? <span className="agentbar-live-dot" /> : null}
            </button>
          ))}
        </div>
      )}

      {/* Conversation panel */}
      {open && convo.length > 0 && (
        <div className="agentbar-convo" ref={scrollRef}>
          {convo.map((m, i) => (
            <div key={i} className={`agentbar-msg ${m.role}`}>
              {m.role === "assistant" && <span className="agentbar-msg-who">{m.agent}</span>}
              <span className={`agentbar-msg-text ${m.err ? "is-err" : ""}`}>{m.content}</span>
            </div>
          ))}
          {sending && (
            <div className="agentbar-msg assistant">
              <span className="agentbar-msg-who">{agent.name}</span>
              <span className="agentbar-msg-text agentbar-typing">
                Working · {elapsed}s{elapsed >= 12 ? " · reviewing live context" : ""}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
